// Edit Client modal — V23
//
// Inline edit of the client's company name and per-client contact list
// (each contact = {name, email}). Used from CompaniesLandingPage so the
// firm can rename a client without delete+re-add, and add/remove
// recipients for the email-send-financials flow.
//
// Mobile responsive: full-screen on phones, modal on desktop.

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Plus, Trash2 } from 'lucide-react';

const COLORS = {
  primary: '#0D7C7C',
  dark: '#1F2937',
  muted: '#6B7280',
  border: '#E5E7EB',
  danger: '#DC2626',
  bg: '#F9FAFB',
};

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EditClientModal({ client, token, onClose, onSaved }) {
  const [company, setCompany] = useState(client.company || '');
  const [contacts, setContacts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Seed from current client. Backwards-compat: if `contacts` is empty
    // but a legacy `contact_email` exists, surface it as the first row.
    const list = Array.isArray(client.contacts) ? [...client.contacts] : [];
    if (list.length === 0 && client.contact_email) {
      list.push({ name: client.name || '', email: client.contact_email });
    }
    setContacts(list);
  }, [client]);

  const addRow = () => setContacts([...contacts, { name: '', email: '' }]);
  const removeRow = (i) => setContacts(contacts.filter((_, idx) => idx !== i));
  const setRow = (i, k, v) => {
    const next = [...contacts];
    next[i] = { ...next[i], [k]: v };
    setContacts(next);
  };

  const save = async () => {
    setError(null);
    const clean = contacts
      .map((c) => ({ name: (c.name || '').trim(), email: (c.email || '').trim().toLowerCase() }))
      .filter((c) => c.email);
    for (const c of clean) {
      if (!c.email.includes('@')) { setError(`Invalid email: ${c.email}`); return; }
      if (!c.name) { setError(`Contact "${c.email}" is missing a name.`); return; }
    }
    if (!company.trim()) { setError('Company name is required.'); return; }

    setSaving(true);
    try {
      await axios.put(`${API}/clients/${client.id}`, {
        company: company.trim(),
        contacts: clean,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="edit-client-modal-overlay"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 'env(safe-area-inset-top) 0 env(safe-area-inset-bottom)',
      }}
      onClick={(e) => e.target === e.currentTarget && !saving && onClose()}
    >
      <div
        data-testid="edit-client-modal"
        style={{
          background: '#fff', borderRadius: 12,
          width: '100%', maxWidth: 520,
          maxHeight: '92vh',
          display: 'flex', flexDirection: 'column',
          margin: '0 12px',
        }}
        className="edit-client-modal"
      >
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <h3 style={{ fontSize: 17, fontWeight: 600, color: COLORS.dark, margin: 0 }}>Edit Client</h3>
          <button
            data-testid="edit-client-modal-close"
            onClick={onClose}
            disabled={saving}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.muted }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: 22, overflowY: 'auto', flex: 1 }}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 6 }}>
              Company Name
            </label>
            <input
              data-testid="edit-client-company"
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14 }}
            />
          </div>

          <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted }}>
              Contacts (financial deliverables go to every contact below)
            </label>
            <button
              data-testid="edit-client-add-contact"
              onClick={addRow}
              style={{
                background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                borderRadius: 8, padding: '6px 10px', fontSize: 12,
                color: COLORS.primary, cursor: 'pointer', display: 'inline-flex',
                alignItems: 'center', gap: 4, fontWeight: 600,
              }}
            >
              <Plus size={12} /> Add contact
            </button>
          </div>

          {contacts.length === 0 && (
            <div style={{
              padding: 14, background: COLORS.bg, borderRadius: 8,
              fontSize: 12, color: COLORS.muted, textAlign: 'center',
            }}>
              No contacts yet. Add at least one to enable the "Send Financials" email flow.
            </div>
          )}

          {contacts.map((c, i) => (
            <div
              key={i}
              data-testid={`edit-client-contact-row-${i}`}
              style={{
                marginBottom: 10, padding: 10, background: COLORS.bg,
                borderRadius: 8, display: 'grid',
                gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'center',
              }}
              className="edit-client-contact-row"
            >
              <input
                data-testid={`edit-client-contact-name-${i}`}
                type="text"
                placeholder="Name"
                value={c.name}
                onChange={(e) => setRow(i, 'name', e.target.value)}
                style={{ padding: '8px 10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13 }}
              />
              <input
                data-testid={`edit-client-contact-email-${i}`}
                type="email"
                placeholder="email@domain.com"
                value={c.email}
                onChange={(e) => setRow(i, 'email', e.target.value)}
                style={{ padding: '8px 10px', borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13 }}
              />
              <button
                data-testid={`edit-client-contact-remove-${i}`}
                onClick={() => removeRow(i)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: COLORS.danger, padding: 4,
                }}
                title="Remove contact"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}

          {error && (
            <div
              data-testid="edit-client-error"
              style={{ marginTop: 12, padding: 10, background: '#FEE2E2', color: COLORS.danger, borderRadius: 6, fontSize: 13 }}
            >
              {error}
            </div>
          )}
        </div>

        <div style={{ padding: '16px 22px', borderTop: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            data-testid="edit-client-cancel"
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '10px 20px', borderRadius: 8, border: `1px solid ${COLORS.border}`,
              background: '#fff', color: COLORS.dark, fontSize: 14, fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            data-testid="edit-client-save"
            onClick={save}
            disabled={saving}
            style={{
              padding: '10px 20px', borderRadius: 8, border: 'none',
              background: COLORS.primary, color: '#fff', fontSize: 14, fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
